import { SummaryDisplay, ISummaryItem } from "src/components/Summary/SummaryDisplay";
import { IFullFlight, IFlightFlagSubDoc } from "@vmosolutions/oc-interfaces";
import { Theme, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IAirportOptionMap } from "src/redux/reducers/StructuresReducer";

/* ----------
 * Interfaces
 * ---------- */

interface IFlightSummaryProps {
  id: string;
  windowProps: {
    flights: IFullFlight[];
  };
}

/** Used to indicate what custom keys/properties/fields to display */
interface ICustomField {
  /** The DB key for referencing the data */
  key: string;
  /** What type of data is the value? */
  type: CustomFieldType;
  /** How to label the data in a visual context */
  label: string;
}

/** Interface for user preferences, for the flight summary display */
interface IUserPrefs {
  standard: {
    flightNumber: boolean;
    origin: boolean;
    destination: boolean;
    customer: boolean;
    aircraftRegistration: boolean;
    aircraftType: boolean;
    flags: boolean;
  };
  custom?: ICustomField[];
}

/* -----------
 * Enumerators
 * ----------- */

/** Specifies what kind of data the custom field is. */
enum CustomFieldType {
  string,
  number,
}

/* ----------------
 * Static Constants
 * ---------------- */

/** Map the standard keys to standard labels */
const standardKeyLabelMap = {
  flightNumber: "Flight Number",
  origin: "Origin",
  destination: "Destination",
  customer: "Customer",
  aircraftRegistration: "Aircraft Registration",
  aircraftType: "Aircraft Type",
  flags: "Flags",
};

/** Default User Preferences for displaying properties in the summary */
const defaultUserPrefs: IUserPrefs = {
  standard: {
    flightNumber: true,
    origin: true,
    destination: true,
    customer: true,
    flags: true,
    aircraftRegistration: true,
    aircraftType: true,
  },
};

/* ---------
 * Functions
 * --------- */

/**
 * A Mosaic Page Component. Retrieves data for a list of specified flight IDs according to user preferences,
 * and passes that data to a SummaryDisplay component in the form of an ISummaryItem array.
 * @param props - Contains mosaic properties and windows props containing a flightId. See [[IFlightSummaryProps]]
 */
const FlightSummary = (props: IFlightSummaryProps): JSX.Element => {
  const { id } = props;
  const { flights } = props.windowProps; // Flights passed in
  const theme: Theme = useTheme();

  const isICAO = useSelector((state: IRootState) => state.authentication.appSettings.blockInfo.icao);
  const airportOptions = useSelector((state: IRootState) => state.structures.airportOptions);
  /** User preferences for what data to display in the summary */
  // const prefs: IUserPrefs = // some code that retrieves from DB
  const prefs: IUserPrefs = defaultUserPrefs; // using default preferences until user preferences epic is started

  /** List of Summary Items */
  const summaryItems: ISummaryItem[] = [];

  /** All the enabled standard keys */
  const standardKeys = Object.entries(prefs.standard)
    .filter((pref) => pref[1] === true)
    .map((pref) => pref[0]);

  if (flights) {
    for (const flight of flights) {
      for (const untypedKey of standardKeys) {
        const key = untypedKey as keyof typeof standardKeyLabelMap;
        try {
          const label: string = standardKeyLabelMap[key];
          const value = getValueOfKey(key, flight, isICAO, airportOptions);
          // Debug.log(value);
          if (value) summaryItems.push({ key, value, label });
          else continue;
        } catch (e) {
          // TODO: Add error handling/logging here
        }
      }
      if (prefs.custom) {
        for (const pref of prefs.custom) {
          try {
            const prefKey = pref.key as keyof typeof flight;
            if (flight[prefKey] && flight[prefKey].toString().length > 0)
              summaryItems.push({
                key: pref.key,
                value: pref.type === CustomFieldType.string ? (prefKey as string) : (flight[prefKey] as number),
                label: pref.label,
              });
          } catch (e) {
            // TODO: Add error handling/logging here
          }
        }
      }
    }
  }

  // Create the title to pass in based on flight numbers
  const title = `Summary of ${flights.length > 1 ? "Flights" : "Flight"} ${summaryItems
    .filter((item) => item.key === "flightNumber")
    .map((item) => item.value)
    .join(", ")}`;
  const backgroundColor = theme.custom.timeline.contextListBackground;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        justifyContent: "center",
        width: "100%",
        height: "100%",
      }}
    >
      <SummaryDisplay
        mosaicId={id}
        flights={flights}
        items={summaryItems.filter((item) => item.key !== "flightNumber")}
        title={title}
        color={backgroundColor}
      />
    </div>
  );
};

/**
 * Returns the given property of the given flight
 * @param key - Property of flight to retrieve
 * @param flight - Flight to retrieve property value from
 */
const getValueOfKey = (
  key: any,
  flight: IFullFlight,
  isICAO: boolean,
  airportOptions: IAirportOptionMap
): string | number => {
  switch (key) {
    case "aircraftRegistration":
      return flight.aircraft.registration || "No Registration";
    case "aircraftType":
      return flight?.aircraft?.aircraftType?.title ?? "No Type Present";
    case "customer":
      return undefined; // customer is not implemented in IFlight yet.
    case "flags":
      return !flight.flags || flight.flags.length === 0
        ? "No Flags"
        : flight.flags.map((flag: IFlightFlagSubDoc) => flag.title).join(", ");
    case "origin":
      return isICAO ? flight.origin : (airportOptions[flight.origin]?.iata ?? "");
    case "destination":
      return isICAO ? flight.destination : (airportOptions[flight.destination]?.iata ?? "");
    default:
      return !flight[key as keyof typeof flight] || flight[key as keyof typeof flight].toString().length === 0
        ? "N/A"
        : flight[key as keyof typeof flight].toString();
  }
};

export default FlightSummary;
